import { BottomNavigation, BottomNavigationAction, Box, Container, Paper, Stack, TextareaAutosize, Typography } from "@mui/material";
import { LogementPageOutletContext, ViewKind } from "../LogementPageBase";
import { LogementDivider } from "./LogementTabContent";
import { useNavigate, useOutletContext } from "react-router";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { diagnosticQuestionnaire, diagnosticQuestions } from "../../../dataModel/generated/questionnaire";
import { LogementAccessor } from "../../../services/LogementAccessor";
import React from "react";

export interface CommentsTabContentProps {
    readonly viewKind: ViewKind;
}

interface CommentItem {
    readonly sectionLabel: string;
    readonly subTitle: string | undefined;
    readonly id: string;
    readonly condition?: (accessor: LogementAccessor) => boolean;
}

const commentItems: ReadonlyArray<CommentItem> = (() => {
    // First pass: find last comment per Titre1 section
    const lastCommentBySection: Record<string, string> = {};
    let currentTitre1: string | undefined;

    for (const q of diagnosticQuestionnaire) {
        if (q.kind === 'Titre1') {
            currentTitre1 = q.label;
        } else if (q.kind === 'Texte' && q.id && currentTitre1) {
            lastCommentBySection[currentTitre1] = q.id;
        }
    }

    // Second pass: build items
    const items: CommentItem[] = [];
    currentTitre1 = undefined;
    let lastTitre2: string | undefined;

    for (const q of diagnosticQuestionnaire) {
        if (q.kind === 'Titre1') {
            currentTitre1 = q.label;
            lastTitre2 = undefined;
        } else if (q.kind === 'Titre2') {
            lastTitre2 = q.label;
        } else if (q.kind === 'Texte' && q.id) {
            const question = diagnosticQuestions[q.id];
            const isLastCommentOfSection = lastCommentBySection[currentTitre1 ?? ''] === q.id;

            items.push({
                sectionLabel: currentTitre1 ?? '',
                subTitle: isLastCommentOfSection ? undefined : lastTitre2,
                id: q.id,
                condition: question?.condition
            });
            lastTitre2 = undefined;
        }
    }

    // Reorganize: for each section, put global comments (subTitle undefined) first, then subsection comments
    const reorganized: CommentItem[] = [];
    const sections = Array.from(new Set(items.map(i => i.sectionLabel)));

    for (const section of sections) {
        const sectionItems = items.filter(i => i.sectionLabel === section);
        const globalItems = sectionItems.filter(i => i.subTitle === undefined);
        const subsectionItems = sectionItems.filter(i => i.subTitle !== undefined);

        reorganized.push(...globalItems, ...subsectionItems);
    }

    return reorganized;
})();

export const CommentsTabContent = (props: CommentsTabContentProps) => {
    const { logementAccessor } = useOutletContext() as LogementPageOutletContext;
    const navigate = useNavigate();
    const goToNext = () => {
        navigate("../documents");
    };
    const goToPrevious = () => {
        navigate("../diagnostic");
    };

    // Build list of visible comments and track their displayed titles
    const visibleComments: Array<{ item: CommentItem; index: number }> = [];
    for (let i = 0; i < commentItems.length; i++) {
        const item = commentItems[i];
        const isVisible = item.condition ? item.condition(logementAccessor) : true;
        if (isVisible) {
            visibleComments.push({ item, index: visibleComments.length });
        }
    }

    return (<Box sx={{ pb: 7 }}><Stack spacing={1}>
        {visibleComments.map(({ item, index: visibleIndex }) => {
            const showDivider = visibleIndex === 0 || visibleComments[visibleIndex - 1].item.sectionLabel !== item.sectionLabel;
            const showSubtitle = item.subTitle && (visibleIndex === 0 || visibleComments[visibleIndex - 1].item.subTitle !== item.subTitle);
            const accessor = (logementAccessor as Record<string, any>)[item.id];

            return <React.Fragment key={item.id}>
                {showDivider && <LogementDivider text={item.sectionLabel}/>}
                {showSubtitle && <Typography variant="subtitle2" sx={{mt: 1}}>
                    {item.subTitle}
                </Typography>}
                <TextareaAutosize
                    disabled={props.viewKind === ViewKind.Read}
                    minRows={5}
                    value={accessor?.get() ?? ""}
                    onChange={(ev) => accessor?.set(ev.target.value)}
                    style={{width: "100%", resize: "none"}}
                />
            </React.Fragment>;
        })}
    </Stack>
    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
        <Container sx={{p: "1 1"}} maxWidth="md">
          <BottomNavigation
            showLabels
          >
            <BottomNavigationAction label="Précédent" icon={<NavigateBefore />} onClick={goToPrevious} />
            <BottomNavigationAction label="Suivant" icon={<NavigateNext />} onClick={goToNext} />
          </BottomNavigation>
        </Container>
      </Paper>
    </Box>);
}
